import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useToast } from '@/components/ui/use-toast'
import { Classroom } from '@/interface/types/classroom'
import { Loader2, Plus, Pencil, Trash2, School } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import {
  CustomPagination as Pagination,
  CustomPaginationContent as PaginationContent,
  CustomPaginationEllipsis as PaginationEllipsis,
  CustomPaginationItem as PaginationItem,
  CustomPaginationLink as PaginationLink,
  CustomPaginationNext as PaginationNext,
  CustomPaginationPrevious as PaginationPrevious,
} from '@/components/ui/custom-pagination'
import { ClassroomForm, ClassroomFormValues } from './ClassroomForm'
import { useAdminStore } from '@/store/adminStore'
import Joyride, { CallBackProps, EVENTS, STATUS, Step } from 'react-joyride'
import { useTranslation } from 'react-i18next'
import { useUser } from '@/hooks/useUser'
import api from '@/lib/axios'

export function ClassroomManagement() {
  const { t } = useTranslation('admin', { keyPrefix: 'classroom-folder' })
  const { toast } = useToast()
  const { user } = useUser()
  const [isAddClassroomOpen, setIsAddClassroomOpen] = useState(false)
  const [isEditClassroomOpen, setIsEditClassroomOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [selectedClassroom, setSelectedClassroom] = useState<Classroom | null>(null)
  const [classroomToDelete, setClassroomToDelete] = useState<Classroom | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(10)
  const [searchQuery, setSearchQuery] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)

  // State for classroom data
  const [classroomsData, setClassroomsData] = useState<{
    data: Classroom[]
    meta: { totalItems: number; totalPages: number; currentPage: number }
  }>({ data: [], meta: { totalItems: 0, totalPages: 1, currentPage: 1 } })
  const [isLoading, setIsLoading] = useState(true)
  const [isError, setIsError] = useState(false)

  // Tour state
  const {
    classroomTour,
    completeClassroomTour,
    setClassroomStep,
  } = useAdminStore()
  const [tourStepIndex, setTourStepIndex] = useState(0)

  // Effect to keep tour state in sync
  useEffect(() => {
    if (classroomTour?.isActive) {
      setTourStepIndex(classroomTour.currentStep || 0)
    }
  }, [classroomTour?.isActive, classroomTour?.currentStep])

  // Tour steps
  const TOUR_STEPS: Step[] = [
    {
      target: '[data-tour="classroom-header"]',
      content: t('classroomManagement.tour.step1'),
      disableBeacon: true,
      placement: 'center',
    },
    {
      target: '[data-tour="add-classroom-button"]',
      content: t('classroomManagement.tour.step2'),
      disableBeacon: true,
      placement: 'bottom',
    },
    {
      target: '[data-tour="classroom-search"]',
      content: t('classroomManagement.tour.step3'),
      disableBeacon: true,
      placement: 'bottom',
    },
    {
      target: '[data-tour="classroom-list"]',
      content: t('classroomManagement.tour.step4'),
      disableBeacon: true,
      placement: 'top',
    },
    {
      target: '[data-tour="classroom-actions"]',
      content: t('classroomManagement.tour.step5'),
      disableBeacon: true,
      placement: 'left',
    },
  ]

  // Function to fetch classrooms
  const fetchClassrooms = async () => {
    setIsLoading(true)
    setIsError(false)

    try {
      // Try getting all classrooms without pagination first
      try {
        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/classrooms`,
          {
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
          }
        )

        if (!response.ok) {
          // If this fails, try the singular endpoint
          throw new Error(
            `API returned ${response.status}: ${response.statusText}`
          )
        }

        const result = await response.json()
        // console.log('All classrooms fetched successfully:', result)

        // Handle the response data
        const allClassrooms = Array.isArray(result) ? result : []

        // Apply pagination manually
        const startIndex = (currentPage - 1) * itemsPerPage
        const endIndex = startIndex + itemsPerPage
        const paginatedClassrooms = allClassrooms.slice(startIndex, endIndex)

        setClassroomsData({
          data: paginatedClassrooms,
          meta: {
            totalItems: allClassrooms.length,
            totalPages: Math.ceil(allClassrooms.length / itemsPerPage),
            currentPage: currentPage,
          },
        })
      } catch (firstError) {
        // console.error(
        //   'First API attempt failed, trying singular endpoint:',
        //   firstError
        // )

        // Try the singular endpoint without pagination
        try {
          const response = await fetch(
            `${import.meta.env.VITE_API_URL}/classroom`,
            {
              headers: {
                'Content-Type': 'application/json',
              },
              credentials: 'include',
            }
          )

          if (!response.ok) {
            // If both fail, we'll create an empty data structure
            throw new Error(
              `API returned ${response.status}: ${response.statusText}`
            )
          }

          const result = await response.json()
          // console.log(
          //   'All classrooms fetched successfully (singular endpoint):',
          //   result
          // )

          // Handle the response data
          const allClassrooms = Array.isArray(result) ? result : []

          // Apply pagination manually
          const startIndex = (currentPage - 1) * itemsPerPage
          const endIndex = startIndex + itemsPerPage
          const paginatedClassrooms = allClassrooms.slice(startIndex, endIndex)

          setClassroomsData({
            data: paginatedClassrooms,
            meta: {
              totalItems: allClassrooms.length,
              totalPages: Math.ceil(allClassrooms.length / itemsPerPage),
              currentPage: currentPage,
            },
          })
        } catch (secondError) {
          // console.error('Both API attempts failed:', secondError)
          // console.log('Setting empty classroom data')

          // If both endpoints fail, set empty data
          setClassroomsData({
            data: [],
            meta: {
              totalItems: 0,
              totalPages: 1,
              currentPage: 1,
            },
          })
        }
      }
    } catch (error) {
      // console.error('Error fetching classrooms:', error)
      setIsError(true)
      toast({
        title: t('classroomManagement.notifications.fetchError.title'),
        description: t('classroomManagement.notifications.fetchError.description'),
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  // Effect to fetch classrooms when component mounts or pagination changes
  useEffect(() => {
    fetchClassrooms()
  }, [currentPage, itemsPerPage])

  // Adjust current page if it exceeds total pages after deletion
  useEffect(() => {
    const totalPages = classroomsData?.meta?.totalPages || 1
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages)
    } else if (totalPages === 0) {
      setCurrentPage(1)
    }
  }, [classroomsData?.meta?.totalPages, currentPage])

  // Function to refresh the classroom list
  const refreshClassrooms = () => {
    fetchClassrooms()
  }

  // Handle creating a new classroom
  const handleCreateClassroom = async (data: ClassroomFormValues) => {
    try {
      // Get the user's establishmentId from userStore (fetched from API)
      let etablissementId = user?.establishmentId || data.etablissementId

      // If establishmentId is missing, fetch it directly from /user-controller/session
      if (!etablissementId || etablissementId.trim() === '') {
        try {
          const response = await api.get('/user-controller/session')
          const userData = response.data
          
          // Handle establishmentId from multiple possible locations in API response
          etablissementId = 
            userData.establishmentId || 
            userData.etablissementId || 
            userData.etablissement?.id ||
            data.etablissementId ||
            ''
        } catch (fetchError) {
          // console.error('Failed to fetch user data:', fetchError)
          // Continue with empty string, will show error below
        }
      }

      if (!etablissementId || etablissementId.trim() === '') {
        // console.error('No establishment ID found. User:', user)
        toast({
          title: t('errors.noEstablishmentId'),
          description: t('errors.noEstablishmentIdDescription'),
          variant: 'destructive',
        })
        return
      }

      // Convert features from string to array
      const featuresArray = data.features
        ? data.features.split(',').map((feature) => feature.trim())
        : []

      // Ensure capacity is a number
      const capacity =
        typeof data.capacity === 'string'
          ? parseInt(data.capacity, 10)
          : data.capacity

      // Create a clean object with only the required properties
      const createData = {
        name: data.name,
        type: data.type,
        capacity: capacity,
        floor: data.floor,
        building: data.building,
        features: featuresArray,
        notes: data.notes,
        isActive: data.isActive,
        etablissementId: etablissementId,
      }

      // console.log('Creating classroom with data:', createData)

      // Make a direct API call instead of using the mutation
      try {
        // Try the singular endpoint first
        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/classroom`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(createData),
          }
        )

        if (!response.ok) {
          throw new Error(
            `API returned ${response.status}: ${response.statusText}`
          )
        }

        // const result = await response.json()
        // console.log('Classroom created successfully:', result)

        // Refresh the classroom list
        fetchClassrooms()
        setIsAddClassroomOpen(false)
        toast({
          title: t('classroomManagement.notifications.createSuccess.title'),
          description: t('classroomManagement.notifications.createSuccess.description'),
        })
      } catch (apiError) {
        // console.error(
        //   'First API attempt failed, trying plural endpoint:',
        //   apiError
        // )

        // Try the plural endpoint as fallback
        try {
          const response = await fetch(
            `${import.meta.env.VITE_API_URL}/classrooms`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              credentials: 'include',
              body: JSON.stringify(createData),
            }
          )

          if (!response.ok) {
            throw new Error(
              `API returned ${response.status}: ${response.statusText}`
            )
          }

          // const result = await response.json()
          // console.log(
          //   'Classroom created successfully (plural endpoint):',
          //   result
          // )

          // Refresh the classroom list
          fetchClassrooms()
          setIsAddClassroomOpen(false)
          toast({
            title: t('classroomManagement.notifications.createSuccess.title'),
            description: t('classroomManagement.notifications.createSuccess.description'),
          })
        } catch (secondApiError) {
          // console.error('Both API attempts failed:', secondApiError)
          throw new Error(
            'Failed to create classroom after trying both endpoints'
          )
        }
      }
    } catch (error) {
      // console.error('Failed to create classroom:', error)
      toast({
        title: t('classroomManagement.notifications.createError.title'),
        description: t('classroomManagement.notifications.createError.description'),
        variant: 'destructive',
      })
    }
  }

  // Handle updating a classroom
  const handleUpdateClassroom = async (data: ClassroomFormValues) => {
    if (!selectedClassroom?.id) return

    try {
      // Convert features from string to array
      const featuresArray = data.features
        ? data.features.split(',').map((feature) => feature.trim())
        : []

      // Ensure capacity is a number
      const capacity =
        typeof data.capacity === 'string'
          ? parseInt(data.capacity, 10)
          : data.capacity

      // Create a clean object with only the required properties
      const updateData = {
        name: data.name,
        type: data.type,
        capacity: capacity,
        floor: data.floor,
        building: data.building,
        features: featuresArray,
        notes: data.notes,
        isActive: data.isActive,
        etablissementId: selectedClassroom.etablissementId,
      }

      // console.log('Updating classroom with data:', {
      //   id: selectedClassroom.id,
      //   data: updateData,
      // })

      // Make a direct API call instead of using the mutation
      try {
        // Try the singular endpoint first
        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/classroom/${selectedClassroom.id}`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(updateData),
          }
        )

        if (!response.ok) {
          throw new Error(
            `API returned ${response.status}: ${response.statusText}`
          )
        }

        await response.json() // Consume response body

        // Refresh the classroom list
        fetchClassrooms()
        setIsEditClassroomOpen(false)
        setSelectedClassroom(null)
        toast({
          title: t('classroomManagement.notifications.updateSuccess.title'),
          description: t('classroomManagement.notifications.updateSuccess.description'),
        })
      } catch (apiError) {
        // console.error(
        //   'First API attempt failed, trying plural endpoint:',
        //   apiError
        // )

        // Try the plural endpoint as fallback
        try {
          const response = await fetch(
            `${import.meta.env.VITE_API_URL}/classroom/${selectedClassroom.id}`,
            {
              method: 'PATCH',
              headers: {
                'Content-Type': 'application/json',
              },
              credentials: 'include',
              body: JSON.stringify(updateData),
            }
          )

          if (!response.ok) {
            throw new Error(
              `API returned ${response.status}: ${response.statusText}`
            )
          }

          // const result = await response.json()
          // console.log(
          //   'Classroom updated successfully (plural endpoint):',
          //   result
          // )

          // Refresh the classroom list
          fetchClassrooms()
          setIsEditClassroomOpen(false)
          setSelectedClassroom(null)
          toast({
            title: t('classroomManagement.notifications.updateSuccess.title'),
            description: t('classroomManagement.notifications.updateSuccess.description'),
          })
        } catch (secondApiError) {
          // console.error('Both API attempts failed:', secondApiError)
          throw new Error(
            'Failed to update classroom after trying both endpoints'
          )
        }
      }
    } catch (error) {
      // console.error('Failed to update classroom:', error)
      toast({
        title: t('classroomManagement.notifications.updateError.title'),
        description: t('classroomManagement.notifications.updateError.description'),
        variant: 'destructive',
      })
    }
  }

  // Open dialog for editing a classroom
  const handleShowClassroom = (classroom: Classroom) => {
    setSelectedClassroom(classroom)
    setIsEditClassroomOpen(true)
  }

  // Open dialog for deleting a classroom
  const handleDeleteClassroom = (classroom: Classroom) => {
    setClassroomToDelete(classroom)
    setIsDeleteDialogOpen(true)
  }

  // Confirm deletion
  const confirmDelete = async () => {
    if (classroomToDelete) {
      try {
        setIsDeleting(true)
        // console.log('Deleting classroom with ID:', classroomToDelete.id)

        // Check if we're deleting the last item on the current page
        const isLastItemOnPage = filteredClassrooms.length === 1
        const shouldGoToPreviousPage = isLastItemOnPage && currentPage > 1

        // Make a direct API call instead of using the mutation
        try {
          // Try the singular endpoint first
          const response = await fetch(
            `${import.meta.env.VITE_API_URL}/classroom/${classroomToDelete.id}`,
            {
              method: 'DELETE',
              headers: {
                'Content-Type': 'application/json',
              },
              credentials: 'include',
            }
          )

          if (!response.ok) {
            throw new Error(
              `API returned ${response.status}: ${response.statusText}`
            )
          }

          // console.log('Classroom deleted successfully')

          // Navigate to previous page if needed
          if (shouldGoToPreviousPage) {
            setCurrentPage(currentPage - 1)
          }

          // Refresh the classroom list
          fetchClassrooms()
          setIsDeleteDialogOpen(false)
          setClassroomToDelete(null)
          toast({
            title: t('classroomManagement.notifications.deleteSuccess.title'),
            description: t('classroomManagement.notifications.deleteSuccess.description'),
          })
        } catch (apiError) {
          // console.error(
          //   'First API attempt failed, trying plural endpoint:',
          //   apiError
          // )

          // Try the plural endpoint as fallback
          try {
            const response = await fetch(
              `${import.meta.env.VITE_API_URL}/classrooms/${classroomToDelete.id}`,
              {
                method: 'DELETE',
                headers: {
                  'Content-Type': 'application/json',
                },
                credentials: 'include',
              }
            )

            if (!response.ok) {
              throw new Error(
                `API returned ${response.status}: ${response.statusText}`
              )
            }

            // console.log('Classroom deleted successfully (plural endpoint)')

            // Navigate to previous page if needed
            if (shouldGoToPreviousPage) {
              setCurrentPage(currentPage - 1)
            }

            // Refresh the classroom list
            fetchClassrooms()
            setIsDeleteDialogOpen(false)
            setClassroomToDelete(null)
            toast({
              title: t('classroomManagement.notifications.deleteSuccess.title'),
              description: t('classroomManagement.notifications.deleteSuccess.description'),
            })
          } catch (secondApiError) {
            // console.error('Both API attempts failed:', secondApiError)
            throw new Error(
              'Failed to delete classroom after trying both endpoints'
            )
          }
        }
      } catch (error) {
        // console.error('Failed to delete classroom:', error)
        toast({
          title: t('classroomManagement.notifications.deleteError.title'),
          description: t('classroomManagement.notifications.deleteError.description'),
          variant: 'destructive',
        })
      } finally {
        setIsDeleting(false)
      }
    }
  }

  // Filter classrooms based on search query
  const filteredClassrooms = classroomsData.data.filter(
    (classroom) =>
      searchQuery === '' ||
      classroom.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      classroom.type.toLowerCase().includes(searchQuery.toLowerCase()) ||
      classroom.building.toLowerCase().includes(searchQuery.toLowerCase())
  )

  // Calculate total pages
  const totalPages = classroomsData?.meta?.totalPages || 1

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = []
    const maxVisiblePages = 5

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total pages is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      // Always show first page
      pages.push(1)

      // Calculate start and end of middle pages
      let start = Math.max(2, currentPage - 1)
      let end = Math.min(totalPages - 1, currentPage + 1)

      // Adjust if we're near the beginning or end
      if (currentPage <= 2) {
        end = 4
      } else if (currentPage >= totalPages - 1) {
        start = totalPages - 3
      }

      // Add ellipsis after first page if needed
      if (start > 2) {
        pages.push('ellipsis1')
      }

      // Add middle pages
      for (let i = start; i <= end; i++) {
        pages.push(i)
      }

      // Add ellipsis before last page if needed
      if (end < totalPages - 1) {
        pages.push('ellipsis2')
      }

      // Always show last page
      if (totalPages > 1) {
        pages.push(totalPages)
      }
    }

    return pages
  }

  // Handle tour callbacks
  const handleJoyrideCallback = (data: CallBackProps) => {
    const { action, index, status, type } = data

    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      completeClassroomTour()
      return
    }

    if (type === EVENTS.STEP_AFTER) {
      setTourStepIndex(index + (action === 'next' ? 1 : -1))
      setClassroomStep(index + (action === 'next' ? 1 : -1))
    }
  }

  if (isError) return <div>{t('classroomManagement.notifications.fetchError.description')}</div>

  return (
    <div className="container mx-auto py-6 px-4">
      <Card className="border shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between" data-tour="classroom-header">
          <div>
            <CardTitle>{t('classroomManagement.title')}</CardTitle>
            <CardDescription>
              {t('classroomManagement.description')}
            </CardDescription>
          </div>
          <div className="flex items-center gap-4">
            <Badge variant="secondary" className="text-sm">
              {t('classroomManagement.badges.totalClassrooms', { count: classroomsData?.meta?.totalItems || 0 })}
            </Badge>

            <Button onClick={() => setIsAddClassroomOpen(true)} data-tour="add-classroom-button">
              <Plus className="mr-2 h-4 w-4" />
              {t('classroomManagement.buttons.addClassroom')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 mb-6">
            <div className="flex-1">
              <div className="flex gap-2">
                <Input
                  placeholder={t('classroomManagement.search.placeholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full"
                  data-tour="classroom-search"
                />
                <Button
                  variant="outline"
                  onClick={refreshClassrooms}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="lucide lucide-refresh-cw"
                    >
                      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                      <path d="M21 3v5h-5" />
                      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                      <path d="M3 21v-5h5" />
                    </svg>
                  )}
                  {t('classroomManagement.buttons.refresh')}
                </Button>
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : (
            <>
              <Table data-tour="classroom-list">
                <TableCaption>
                  {t('classroomManagement.table.caption')}
                </TableCaption>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('classroomManagement.table.headers.name')}</TableHead>
                    <TableHead>{t('classroomManagement.table.headers.type')}</TableHead>
                    <TableHead>{t('classroomManagement.table.headers.capacity')}</TableHead>
                    <TableHead>{t('classroomManagement.table.headers.location')}</TableHead>
                    {/* <TableHead>{t('classroomManagement.table.headers.features')}</TableHead> */}
                    <TableHead>{t('classroomManagement.table.headers.status')}</TableHead>
                    <TableHead className="text-right" data-tour="classroom-actions">{t('classroomManagement.table.headers.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredClassrooms.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8">
                        {t('classroomManagement.table.noClassrooms')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredClassrooms.map((classroom) => (
                      <TableRow key={classroom.id}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <School className="h-4 w-4 text-muted-foreground" />
                            {classroom.name}
                          </div>
                        </TableCell>
                        <TableCell>{classroom.type}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{classroom.capacity}</Badge>
                        </TableCell>
                        <TableCell className="max-w-xs truncate">{`${classroom.floor}, ${classroom.building}`}</TableCell>
                        {/* <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {classroom.features
                              ?.slice(0, 2)
                              .map((feature, index) => (
                                <Badge key={index} variant="outline">
                                  {feature}
                                </Badge>
                              ))}
                            {classroom.features?.length > 2 && (
                              <Badge variant="outline">
                                {t('classroomManagement.table.featuresMore', { count: classroom.features.length - 2 })}
                              </Badge>
                            )}
                          </div>
                        </TableCell> */}
                        <TableCell>
                          {classroom.isActive ? (
                            <Badge variant="success">{t('classroomManagement.table.status.active')}</Badge>
                          ) : (
                            <Badge variant="secondary">{t('classroomManagement.table.status.inactive')}</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleShowClassroom(classroom)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteClassroom(classroom)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-4">
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious
                          onClick={() =>
                            setCurrentPage((prev) => Math.max(1, prev - 1))
                          }
                          disabled={currentPage === 1}
                        />
                      </PaginationItem>

                      {getPageNumbers().map((page, index) =>
                        page === 'ellipsis1' || page === 'ellipsis2' ? (
                          <PaginationItem key={`ellipsis-${index}`}>
                            <PaginationEllipsis />
                          </PaginationItem>
                        ) : (
                          <PaginationItem key={page}>
                            <PaginationLink
                              isActive={currentPage === page}
                              onClick={() => setCurrentPage(Number(page))}
                            >
                              {page}
                            </PaginationLink>
                          </PaginationItem>
                        )
                      )}

                      <PaginationItem>
                        <PaginationNext
                          onClick={() =>
                            setCurrentPage((prev) =>
                              Math.min(totalPages, prev + 1)
                            )
                          }
                          disabled={currentPage === totalPages}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Add Classroom Dialog */}
      <Dialog open={isAddClassroomOpen} onOpenChange={setIsAddClassroomOpen}>
        <DialogContent className="sm:max-w-[600px] border shadow-sm">
          <DialogHeader>
            <DialogTitle>{t('classroomManagement.dialogs.add.title')}</DialogTitle>
          </DialogHeader>
          <ClassroomForm
            onSubmit={handleCreateClassroom}
            onCancel={() => setIsAddClassroomOpen(false)}
            submitLabel={t('classroomManagement.buttons.addClassroom')}
          />
        </DialogContent>
      </Dialog>
      {/* Edit Classroom Dialog */}
      <Dialog open={isEditClassroomOpen} onOpenChange={setIsEditClassroomOpen}>
        <DialogContent className="sm:max-w-[600px] border shadow-sm">
          <DialogHeader>
            <DialogTitle>{t('classroomManagement.dialogs.edit.title')}</DialogTitle>
          </DialogHeader>
          <ClassroomForm
            initialData={selectedClassroom || undefined}
            onSubmit={handleUpdateClassroom}
            onCancel={() => setIsEditClassroomOpen(false)}
            submitLabel={t('classroomManagement.buttons.updateClassroom')}
          />
        </DialogContent>
      </Dialog>
      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[425px] border shadow-sm">
          <DialogHeader>
            <DialogTitle>{t('classroomManagement.dialogs.delete.title')}</DialogTitle>
          </DialogHeader>
          <p>
            {t('classroomManagement.dialogs.delete.description', { name: classroomToDelete?.name })}
          </p>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              {t('classroomManagement.dialogs.delete.cancel')}
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={confirmDelete}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="mr-2 h-4 w-4" />
              )}
              {t('classroomManagement.dialogs.delete.confirm')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Tour Component */}
      <Joyride
        steps={TOUR_STEPS}
        run={classroomTour?.isActive}
        continuous
        hideCloseButton
        showProgress
        showSkipButton
        stepIndex={tourStepIndex}
        callback={handleJoyrideCallback}
        disableOverlayClose
        disableCloseOnEsc
        styles={{
          options: {
            primaryColor: '#525FE1',
            zIndex: 10000,
            arrowColor: '#ffffff',
            backgroundColor: '#ffffff',
            overlayColor: 'rgba(0, 0, 0, 0.5)',
          },
          buttonNext: {
            backgroundColor: '#525FE1',
            fontSize: '14px',
            padding: '8px 16px',
            borderRadius: '6px',
          },
          buttonBack: {
            color: '#666',
            marginRight: 12,
            fontSize: '14px',
          },
          buttonSkip: {
            color: '#666',
            fontSize: '14px',
          },
          tooltip: {
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            padding: '16px',
          },
          tooltipContainer: {
            textAlign: 'left',
          },
          tooltipContent: {
            padding: '0',
          },
          spotlight: {
            backgroundColor: 'transparent',
            borderRadius: '4px',
          }
        }}
      />
    </div>
  )
}
